import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ArrowLeft, Calendar, User, Eye, Clock, Tag } from 'lucide-react'
import { useParams, useNavigate } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { publicationApi } from '@/lib/client'
import { ShareButton } from '@/components/ShareButton'

export default function NewsDetailPage() {
  const { slug } = useParams()
  const navigate = useNavigate()

  // Fetch single news article by slug
  const { data: newsData, isLoading, error } = useQuery({
    queryKey: ['publication', slug],
    queryFn: () => publicationApi.getPublicationBySlug(slug!),
    enabled: !!slug
  })

  const article = newsData?.data

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-6 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
              <div className="h-64 bg-gray-200 rounded-xl mb-8"></div>
              <div className="space-y-4">
                <div className="h-6 bg-gray-200 rounded w-3/4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                  <div className="h-4 bg-gray-200 rounded w-4/5"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-6 py-8">
          <div className="max-w-4xl mx-auto">
            <Button
              onClick={() => navigate('/news')}
              className="mb-6 bg-white/20 hover:bg-white/30 text-blue-600 border border-blue-200"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Kembali ke Berita
            </Button>
            <Card className="border-0 shadow-lg">
              <CardContent className="p-12 text-center">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Berita tidak ditemukan</h3>
                <p className="text-gray-500 mb-6">Artikel yang Anda cari mungkin telah dihapus atau tidak tersedia.</p>
                <Button onClick={() => navigate('/news')}>
                  Kembali ke Halaman Berita
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Header with Back Button */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 backdrop-blur-sm bg-white/90">
        <div className="container mx-auto px-6 py-4">
          <Button
            onClick={() => navigate('/news')}
            variant="ghost"
            className="hover:bg-gray-100 text-gray-600"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Kembali ke Berita
          </Button>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Article Header */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Badge variant="secondary" className="bg-blue-100 text-blue-800 border-blue-200">
                {article.type}
              </Badge>
              {article.categories && article.categories.map((category: { id: string; name: string }) => (
                <Badge key={category.id} variant="outline" className="border-gray-300 text-gray-600">
                  <Tag className="h-3 w-3 mr-1" />
                  {category.name}
                </Badge>
              ))}
            </div>

            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
              {article.title}
            </h1>

            {article.summary && (
              <p className="text-xl text-gray-600 mb-6 leading-relaxed">
                {article.summary}
              </p>
            )}

            {/* Article Meta */}
            <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500 mb-6 pb-6 border-b border-gray-200">
              {article.publishedAt && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-blue-500" />
                  <span>{new Date(article.publishedAt).toLocaleDateString('id-ID', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}</span>
                </div>
              )}
              {article.author && (
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-green-500" />
                  <span>{article.author.name}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-orange-500" />
                <span>{article.readTime || '5 menit baca'}</span>
              </div>
            </div>

            {/* Share Section */}
            <div className="flex items-center justify-between mb-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Bagikan Berita Ini</h3>
                <p className="text-sm text-gray-600">Sebarkan informasi penting untuk masyarakat</p>
              </div>
              <ShareButton 
                title={article.title}
                description={article.summary}
                variant="default"
              />
            </div>
          </div>

          {/* Featured Image */}
          {article.featuredImage && (
            <div className="mb-8">
              <div className="aspect-video bg-gray-100 rounded-xl overflow-hidden shadow-lg">
                <img 
                  src={article.featuredImage} 
                  alt={article.title}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          )}

          {/* Article Content */}
          <Card className="border-0 shadow-lg mb-8">
            <CardContent className="p-8">
              <div 
                className="prose prose-lg max-w-none text-gray-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content || '' }}
              />
            </CardContent>
          </Card>

          {/* Tags */}
          {article.tags && article.tags.length > 0 && (
            <Card className="border-0 shadow-lg mb-8">
              <CardHeader>
                <CardTitle className="text-lg">Tags</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {article.tags.map((tag, index: number) => (
                    <Badge key={index} variant="outline" className="border-gray-300 text-gray-600">
                      {typeof tag === 'string' ? tag : tag.name}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Bottom Share Section */}
          <Card className="border-0 shadow-lg mb-8">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Artikel Bermanfaat?</h3>
                  <p className="text-sm text-gray-600">Bagikan dengan teman dan keluarga</p>
                </div>
                <ShareButton 
                  title={article.title}
                  description={article.summary}
                  variant="outline"
                />
              </div>
            </CardContent>
          </Card>

          {/* Navigation */}
          <div className="flex justify-center">
            <Button
              onClick={() => navigate('/news')}
              variant="outline"
              className="border-gray-300 hover:bg-gray-50"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Kembali ke Semua Berita
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}